// src/routes/index.tsx
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthProvider } from "../components/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProtectedLayout from "../components/ProtectedLayout";
import LoginLayout from "../components/LoginLayout";
import Dashboard from "../components/Dashboard";
import CustomerPhoneNumbersList from "../components/CustomerPhoneNumbersList";
import CustomerEmailAddressesList from "../components/CustomerEmailAddressesList";
import CustomerEmailMessages from "../components/CustomerEmailMessages";
import CustomerSmsMessages from "../components/CustomerSmsMessages";
import Login from "../components/Login";
import LoginCallback from "../components/LoginCallback";
import NotAuthorized from "../components/NotAuthorized";
import Companies from "components/Companies";
import CompanyLayout from "../components/Company/CompanyLayout";
import CompanyOverview from "../components/Company/CompanyOverview";
import CompanyApiKeys from "../components/Company/CompanyApiKeys";

// Root layout with single AuthProvider
const RootLayout: React.FC = () => {
  return (
    <AuthProvider>
      <div className="App">
        <Header />
        <LoginLayout />
        <ProtectedLayout />
        <Footer />
        <ToastContainer position="top-right" />
      </div>
    </AuthProvider>
  );
};

// Keeps links to the old /customers/... pages working.
const LegacyRedirect: React.FC<{ to: (params: Record<string, string | undefined>) => string }> = ({ to }) => {
  const params = useParams();
  return <Navigate to={to(params)} replace />;
};

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: "/dashboard",
        element: <Dashboard />,
      },
      {
        path: "/companies",
        element: <Companies />,
      },
      {
        path: "/companies/:customerId",
        element: <CompanyLayout />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <CompanyOverview /> },
          { path: "api-keys", element: <CompanyApiKeys /> },
          { path: "email-messages", element: <CustomerEmailMessages /> },
          { path: "email-addresses", element: <CustomerEmailAddressesList /> },
          { path: "sms-messages", element: <CustomerSmsMessages /> },
          { path: "phone-numbers", element: <CustomerPhoneNumbersList /> },
        ],
      },
      {
        path: "/customers/:customerId",
        element: <LegacyRedirect to={(p) => `/companies/${p.customerId}`} />,
      },
      {
        path: "/customers/:customerId/communications/apikeys/:apiKeyId",
        element: <LegacyRedirect to={(p) => `/companies/${p.customerId}/overview?apiKeyId=${p.apiKeyId}`} />,
      },
      {
        path: "/customers/:customerId/emailmessage/apikeys/:apiKeyId",
        element: <LegacyRedirect to={(p) => `/companies/${p.customerId}/email-messages?apiKeyId=${p.apiKeyId}`} />,
      },
      {
        path: "/customers/:customerId/smsmessage/apikeys/:apiKeyId",
        element: <LegacyRedirect to={(p) => `/companies/${p.customerId}/sms-messages?apiKeyId=${p.apiKeyId}`} />,
      },
      {
        path: "/customers/:customerId/phone-numbers",
        element: <LegacyRedirect to={(p) => `/companies/${p.customerId}/phone-numbers`} />,
      },
      {
        path: "/customers/:customerId/email-addresses",
        element: <LegacyRedirect to={(p) => `/companies/${p.customerId}/email-addresses`} />,
      },
      {
        path: "/login",
        element: <Login />,
      },
      {
        path: "/login/callback",
        element: <LoginCallback />,
      },
      {
        path: "/not-authorized",
        element: <NotAuthorized />,
      },
      {
        path: "*",
        element: <Navigate to="/dashboard" replace />,
      },
    ],
  },
]);
